import {
	Box,
	type CircularProgressProps,
	keyframes,
	useMediaQuery,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useId } from 'react'
import { LOADING_BOX, TRIANGLE_PATH } from './loading-shapes'

export type LoadingProps = {
	size?: number
	color?: CircularProgressProps['color']
	variant?: 'overlay' | 'inline' | 'page'
	contained?: boolean
	backdrop?: boolean
	delay?: number
	label?: string
}

const LOOP = '3.2s'
const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)'

const enter = keyframes`
	from { opacity: 0; transform: scale(0.9); }
	to { opacity: 1; transform: scale(1); }
`
const fadeIn = keyframes`
	from { opacity: 0; }
	to { opacity: 1; }
`
const pulse = keyframes`
	from { opacity: 1; }
	to { opacity: 0.5; }
`
// One loop: draw the outline, wash the fill in at 45°, pop, sweep a light
// across, then grow and blur out.
const draw = keyframes`
	0% { stroke-dashoffset: 1; }
	45%, 100% { stroke-dashoffset: 0; }
`
const wash = keyframes`
	0%, 45% { transform: translateX(0); }
	62%, 100% { transform: translateX(26px); }
`
const shine = keyframes`
	0%, 64% { transform: translateX(-40px); }
	80%, 100% { transform: translateX(44px); }
`
const life = keyframes`
	0%, 60% { transform: scale(1); opacity: 1; filter: blur(0); }
	66% { transform: scale(1.06); }
	72%, 80% { transform: scale(1); opacity: 1; filter: blur(0); }
	100% { transform: scale(1.18); opacity: 0; filter: blur(1.5px); }
`

const layout = {
	overlay: { position: 'absolute', inset: 0, zIndex: 1 },
	page: { position: 'fixed', inset: 0, zIndex: 'modal' },
	inline: { display: 'inline-flex', verticalAlign: 'middle' },
} as const

function Loading({
	size = 120,
	color = 'primary',
	variant = 'overlay',
	contained = false,
	backdrop = false,
	delay = 300,
	label = 'Loading…',
}: LoadingProps) {
	const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)', {
		noSsr: true,
	})
	const id = useId().replace(/[^\w-]/g, '')
	const clipId = `${id}-clip`
	const shineId = `${id}-shine`

	return (
		<Box
			role="status"
			aria-busy="true"
			aria-live="polite"
			sx={[
				{ display: 'grid', placeContent: 'center' },
				layout[variant],
				backdrop && variant !== 'inline'
					? {
							backgroundColor: (theme) =>
								alpha(theme.palette.background.paper, 0.6),
							backdropFilter: 'blur(2px)',
						}
					: {},
			]}
		>
			<Box
				sx={{
					display: 'flex',
					color: color === 'inherit' ? 'inherit' : `${color}.main`,
					animation: `${reduceMotion ? fadeIn : enter} 200ms ease-out ${delay}ms both`,
				}}
			>
				<svg
					role="progressbar"
					aria-label={label}
					width={size}
					height={size}
					viewBox={`0 0 ${LOADING_BOX} ${LOADING_BOX}`}
					overflow="visible"
				>
					{contained && (
						<circle
							cx={LOADING_BOX / 2}
							cy={LOADING_BOX / 2}
							r={LOADING_BOX / 2}
							fill="currentColor"
							fillOpacity={0.16}
						/>
					)}
					{reduceMotion ? (
						<Box
							component="path"
							d={TRIANGLE_PATH}
							fill="currentColor"
							sx={{ animation: `${pulse} 1.5s ease-in-out infinite alternate` }}
						/>
					) : (
						<>
							<defs>
								<clipPath id={clipId}>
									<path d={TRIANGLE_PATH} />
								</clipPath>
								<linearGradient id={shineId}>
									<stop offset="0" stopColor="#fff" stopOpacity={0} />
									<stop offset="0.5" stopColor="#fff" stopOpacity={0.7} />
									<stop offset="1" stopColor="#fff" stopOpacity={0} />
								</linearGradient>
							</defs>
							<Box
								component="g"
								sx={{
									transformOrigin: 'center',
									transformBox: 'view-box',
									animation: `${life} ${LOOP} infinite`,
								}}
							>
								<g clipPath={`url(#${clipId})`}>
									{/* Starts 4 units back from the long edge, else a hairline shows there. */}
									<g transform="rotate(-45 24 24)">
										<Box
											component="rect"
											x={-34}
											y={-30}
											width={54}
											height={108}
											fill="currentColor"
											sx={{
												animation: `${wash} ${LOOP} cubic-bezier(0.3, 0, 0.2, 1) infinite`,
											}}
										/>
									</g>
									<g transform="rotate(20 14 24)">
										<Box
											component="rect"
											x={9}
											y={0}
											width={10}
											height={48}
											fill={`url(#${shineId})`}
											sx={{
												animation: `${shine} ${LOOP} ease-in-out infinite`,
											}}
										/>
									</g>
								</g>
								<Box
									component="path"
									d={TRIANGLE_PATH}
									pathLength={1}
									fill="none"
									stroke="currentColor"
									strokeWidth={2}
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeDasharray={1}
									sx={{
										strokeDashoffset: 1,
										animation: `${draw} ${LOOP} ${EASE} infinite`,
									}}
								/>
							</Box>
						</>
					)}
				</svg>
			</Box>
		</Box>
	)
}

export { Loading, Loading as default }
